import { type RefObject, useEffect, useState, useSyncExternalStore } from 'react';

// A copy of the `--breakpoint-*` values in src/styles/global.css, for JavaScript.
// useMeasure.test.ts compares the two.
export const BREAKPOINTS = {
  sm: 640, // large phones, landscape
  md: 768, // tablets
  lg: 1024, // small laptops — the sidebar drawer/rail threshold
  xl: 1280, // desktops
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** Returns true while the window is at least as wide as the breakpoint. */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`;

  return useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Returns the content width of the element in pixels after each resize, or 0 before the first measure. */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    setWidth(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

/** Returns true after the element first comes into the viewport, or at once when `skip` is true. */
export function useInViewport(ref: RefObject<Element | null>, skip = false): boolean {
  const [seen, setSeen] = useState(skip || typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const element = ref.current;
    if (seen || !element) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, seen]);

  return seen || skip;
}
